import React, { useEffect, useState, useMemo } from "react";
import { Box } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import TicketTable from "../components/TicketTable";
import { useSupervisorHook } from "../hooks";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import GlobalLoadingOverlay from "../components/GlobalLoadingOverlay";
import { useUserMsp } from "../hooks/useUserMsp";
import {
  isAutotask,
  isHalo,
  isConnectWise,
} from "../utils/mspUtils";
import SupervisorApi from "../apis/supervisorApi";
import {
  areStringArraysEqual, areSortEqual, QueueFilterSessionState,
  getQueueFilterSessionStorageKey, readQueueFilterSessionState,
  writeQueueFilterSessionState, areQueueFilterSessionStatesEqual,
  parseQueueUrlState, buildQueueSearch,
} from "../utils/queueUrlState";
import { Ticket } from "../types/supervisorType";
import { isOpenTicketStatus, matchesSelectedStatus } from "../utils/ticketStatusFilter";
import { OPEN_TICKETS_LABEL } from "../navigation/workScopes";
import { ThreeDot } from "react-loading-indicators";
import { isServiceDeskInboxFlagEnabled } from "../utils/serviceDeskInbox";

interface GroupQueueProps {
  techGroupName?: string;
  queueId?: number;
  platformId?: number;
  boardId?: number;
  boardName?: string;
  categoryName?: string;
  categoryId?: number;
}

function normalizeFilterValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value).toLowerCase().trim();
}

function getDefaultQueueStatusFilter(mspType?: string): string[] | undefined {
  return undefined;
}

function extractTicketAccountId(ticket: Ticket): string | null {
  const client = ticket.client as any;
  if (!client) {
    return null;
  }

  if (typeof client === "string" || typeof client === "number") {
    const accountId = normalizeFilterValue(client);
    return accountId || null;
  }

  if (typeof client === "object") {
    const accountId = normalizeFilterValue(client.accountId);
    return accountId || null;
  }

  return null;
}

function areQueueFiltersEqual(
  left: {
    groupName?: string;
    categoryName?: string[];
    status?: string[];
    customer?: string[];
    boardId?: number;
    includeAllStatuses?: boolean;
  },
  right: {
    groupName?: string;
    categoryName?: string[];
    status?: string[];
    customer?: string[];
    boardId?: number;
    includeAllStatuses?: boolean;
  }
) {
  return (
    left.groupName === right.groupName &&
    areStringArraysEqual(left.categoryName, right.categoryName) &&
    areStringArraysEqual(left.status, right.status) &&
    areStringArraysEqual(left.customer, right.customer) &&
    (left.boardId ?? undefined) === (right.boardId ?? undefined) &&
    Boolean(left.includeAllStatuses) === Boolean(right.includeAllStatuses)
  );
}

const GroupQueue: React.FC<GroupQueueProps> = ({
  techGroupName,
  boardId,
  boardName,
  categoryName,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth0();
  const { userMspInfo } = useUserMsp();
  const flags = useFeatureFlag();
  const serviceDeskInboxEnabled = isServiceDeskInboxFlagEnabled(
    flags as Record<string, unknown>
  );
  const defaultStatusFilter = useMemo(
    () => getDefaultQueueStatusFilter(userMspInfo?.msp_type),
    [userMspInfo?.msp_type]
  );

  const groupName = boardId ? boardName : techGroupName;
  const includeAllStatuses = Boolean(
    userMspInfo?.msp_type && isAutotask(userMspInfo.msp_type)
  );
  const queueFilterStorageKey = useMemo(
    () =>
      getQueueFilterSessionStorageKey(
        user?.sub || user?.email,
        userMspInfo?.msp_id
      ),
    [user?.sub, user?.email, userMspInfo?.msp_id]
  );
  const [sessionFilterState, setSessionFilterState] = useState<QueueFilterSessionState>(
    () => readQueueFilterSessionState(queueFilterStorageKey)
  );

  useEffect(() => {
    const nextSessionFilterState = readQueueFilterSessionState(queueFilterStorageKey);
    setSessionFilterState((prevSessionFilterState) =>
      areQueueFilterSessionStatesEqual(
        prevSessionFilterState,
        nextSessionFilterState
      )
        ? prevSessionFilterState
        : nextSessionFilterState
    );
  }, [queueFilterStorageKey]);

  const queueUrlState = useMemo(
    () =>
      parseQueueUrlState(
        location.search,
        defaultStatusFilter,
        sessionFilterState
      ),
    [location.search, defaultStatusFilter, sessionFilterState]
  );
  const initialFilters = boardId
    ? {
        groupName,
        boardId,
        includeAllStatuses,
        status: queueUrlState.status,
        customer: queueUrlState.customer,
      }
    : {
        groupName,
        categoryName: categoryName ? [categoryName] : undefined,
        includeAllStatuses,
        status: queueUrlState.status,
        customer: queueUrlState.customer,
      };

  const {
    isLoading,
    tickets,
    page,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    filters: fetchFilters,
    setFilters: setFetchFilters,
    sort,
    setSort,
    setPage,
    setPageSize,
  } = useSupervisorHook(initialFilters, queueUrlState.page);

  const [displayFilters, setDisplayFilters] = useState<{
    groupName?: string;
    categoryName?: string[];
    status?: string[];
    customer?: string[];
    boardId?: number;
    includeAllStatuses?: boolean;
  }>(
    boardId
      ? {
          groupName,
          boardId,
          includeAllStatuses,
          status: queueUrlState.status,
          customer: queueUrlState.customer,
        }
      : {
          groupName,
          categoryName: categoryName ? [categoryName] : undefined,
          includeAllStatuses,
          status: queueUrlState.status,
          customer: queueUrlState.customer,
        }
  );

  useEffect(() => {
    const nextFilters = boardId
      ? {
          groupName,
          categoryName: undefined,
          boardId,
          includeAllStatuses,
          status: queueUrlState.status,
          customer: queueUrlState.customer,
        }
      : {
          groupName,
          categoryName: categoryName ? [categoryName] : undefined,
          boardId: undefined,
          includeAllStatuses,
          status: queueUrlState.status,
          customer: queueUrlState.customer,
        };

    setFetchFilters((prevFilters: { groupName?: string; categoryName?: string[]; status?: string[]; customer?: string[]; boardId?: number; includeAllStatuses?: boolean; refreshToken?: string }) => {
      if (areQueueFiltersEqual(prevFilters, nextFilters) && prevFilters.refreshToken === undefined) {
        return prevFilters;
      }

      return {
        ...prevFilters,
        ...nextFilters,
        refreshToken: undefined,
      };
    });
    setDisplayFilters((prev) =>
      areQueueFiltersEqual(prev, nextFilters) ? prev : nextFilters
    );
    setSort((prev) => (areSortEqual(prev, queueUrlState.sort) ? prev : queueUrlState.sort));
    setPage((prev) => (prev === queueUrlState.page ? prev : queueUrlState.page));
    setPageSize((prev) =>
      prev === queueUrlState.pageSize ? prev : queueUrlState.pageSize
    );
  }, [
    groupName,
    boardId,
    categoryName,
    includeAllStatuses,
    queueUrlState,
    setFetchFilters,
    setPage,
    setPageSize,
    setSort,
  ]);

  const [connectWiseStatusOptions, setConnectWiseStatusOptions] = useState<string[]>([]);
  const [autotaskStatusOptions, setAutotaskStatusOptions] = useState<string[]>([]);
  const [haloStatusOptions, setHaloStatusOptions] = useState<string[]>([]);
  const [lastSettledTickets, setLastSettledTickets] = useState<Ticket[]>([]);
  const mspType = String(userMspInfo?.msp_type || "");

  const supervisorApi = useMemo(() => new SupervisorApi(), []);

  useEffect(() => {
    let isMounted = true;

    const loadConnectWiseStatusOptions = async () => {
      if (!userMspInfo?.msp_type || !isConnectWise(userMspInfo.msp_type)) {
        if (isMounted) {
          setConnectWiseStatusOptions([]);
        }
        return;
      }

      try {
        const response = await supervisorApi.getTicketCreateOptions(groupName);
        const options = Array.isArray(response?.statusOptions)
          ? response.statusOptions
              .map((option: any) => String(option?.label || "").trim())
              .filter((label: string) => Boolean(label))
          : [];

        if (isMounted) {
          setConnectWiseStatusOptions(Array.from(new Set(options)));
        }
      } catch (error) {
        console.error("Failed to load ConnectWise status options:", error);
        if (isMounted) {
          setConnectWiseStatusOptions([]);
        }
      }
    };

    loadConnectWiseStatusOptions();
    return () => {
      isMounted = false;
    };
  }, [userMspInfo?.msp_type, supervisorApi, groupName]);

  useEffect(() => {
    let isMounted = true;

    const loadAutotaskStatusOptions = async () => {
      if (!userMspInfo?.msp_type || !isAutotask(userMspInfo.msp_type)) {
        if (isMounted) {
          setAutotaskStatusOptions([]);
        }
        return;
      }

      try {
        const response = await supervisorApi.getTicketStatusOptions();
        const options = Array.isArray(response?.statusOptions)
          ? response.statusOptions
              .map((option: any) => String(option?.label || "").trim())
              .filter((label: string) => Boolean(label))
          : [];

        if (isMounted) {
          setAutotaskStatusOptions(Array.from(new Set(options)));
        }
      } catch (error) {
        console.error("Failed to load Autotask status options:", error);
        if (isMounted) {
          setAutotaskStatusOptions([]);
        }
      }
    };

    loadAutotaskStatusOptions();
    return () => {
      isMounted = false;
    };
  }, [userMspInfo?.msp_type, supervisorApi]);

  useEffect(() => {
    let isMounted = true;

    const loadHaloStatusOptions = async () => {
      if (!userMspInfo?.msp_type || !isHalo(userMspInfo.msp_type)) {
        if (isMounted) {
          setHaloStatusOptions([]);
        }
        return;
      }

      try {
        const response = await supervisorApi.getTicketCreateOptions({
          queueName: groupName,
          categoryName,
        });
        const options = Array.isArray(response?.statusOptions)
          ? response.statusOptions
              .map((option: any) => String(option?.label || "").trim())
              .filter((label: string) => Boolean(label))
          : [];

        if (isMounted) {
          setHaloStatusOptions(Array.from(new Set(options)));
        }
      } catch (error) {
        console.error("Failed to load Halo status options:", error);
        if (isMounted) {
          setHaloStatusOptions([]);
        }
      }
    };

    loadHaloStatusOptions();
    return () => {
      isMounted = false;
    };
  }, [userMspInfo?.msp_type, supervisorApi, groupName, categoryName]);

  useEffect(() => {
    if (!isLoading) {
      setLastSettledTickets(tickets || []);
    }
  }, [isLoading, tickets]);

  const navigateToTicketDetail = (ticketId: string | number) => {
    const returnTo = `${location.pathname}${location.search}`;
    const detailSearch = new URLSearchParams();
    detailSearch.set("returnTo", returnTo);
    navigate(`/supervisor/tickets/${ticketId}?${detailSearch.toString()}`);
  };

  const handleSortChange = (
    newSort: { attribute: string; order: "ASC" | "DESC" }[]
  ) => {
    const nextSort: { attribute: string; order: "ASC" | "DESC" }[] =
      newSort.length > 0
        ? newSort
        : [{ attribute: "created_at", order: "DESC" }];
    navigate({
      pathname: location.pathname,
      search: buildQueueSearch({
        status: displayFilters.status,
        customer: displayFilters.customer,
        page: 1,
        pageSize,
        sort: nextSort,
      }),
    });
  };

  const handleFilterChange = (nextFilters: {
    groupName?: string;
    categoryName?: string[];
    status?: string[];
    customer?: string[];
  }) => {
    const normalizedFilters: {
      groupName?: string;
      categoryName?: string[];
      status?: string[];
      customer?: string[];
      boardId?: number;
      includeAllStatuses?: boolean;
    } = {
      ...nextFilters,
      groupName,
      includeAllStatuses,
      ...(categoryName ? { categoryName: [categoryName] } : { categoryName: undefined }),
      ...(boardId ? { boardId } : { boardId: undefined }),
    };
    const nextSessionFilterState = {
      status: normalizedFilters.status,
      customer: normalizedFilters.customer,
    };
    writeQueueFilterSessionState(queueFilterStorageKey, nextSessionFilterState);
    setSessionFilterState((prevSessionFilterState) =>
      areQueueFilterSessionStatesEqual(
        prevSessionFilterState,
        nextSessionFilterState
      )
        ? prevSessionFilterState
        : nextSessionFilterState
    );
    navigate({
      pathname: location.pathname,
      search: buildQueueSearch({
        status: normalizedFilters.status,
        customer: normalizedFilters.customer,
        page: 1,
        pageSize,
        sort,
      }),
    });
  };

  const handleListPageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    navigate({
      pathname: location.pathname,
      search: buildQueueSearch({
        status: displayFilters.status,
        customer: displayFilters.customer,
        page: value,
        pageSize,
        sort,
      }),
    });
  };

  const handleListPageSizeChange = (nextPageSize: number) => {
    if (!Number.isFinite(nextPageSize) || nextPageSize <= 0) {
      return;
    }

    navigate({
      pathname: location.pathname,
      search: buildQueueSearch({
        status: displayFilters.status,
        customer: displayFilters.customer,
        page: 1,
        pageSize: nextPageSize,
        sort,
      }),
    });
  };

  const handleRefreshTickets = () => {
    setFetchFilters((prevFilters: { groupName?: string; categoryName?: string[]; status?: string[]; customer?: string[]; boardId?: number; includeAllStatuses?: boolean; refreshToken?: string }) => ({
      ...prevFilters,
      refreshToken: String(Date.now()),
    }));
  };

  const buildTicketDetailPath = (ticketId: string) => {
    const returnTo = `${location.pathname}${location.search}`;
    const detailSearch = new URLSearchParams();
    detailSearch.set("returnTo", returnTo);
    return `/supervisor/tickets/${ticketId}?${detailSearch.toString()}`;
  };

  /**
   * ConnectWise list filtering must stay aligned with server-side status matching.
   * Never use strict exact-label-only matching here: PSA labels like "Complete" vs "Resolved"
   * normalize differently than checkbox labels, which hides valid rows after a status filter.
   */
  const isConnectWisePsa = Boolean(
    userMspInfo?.msp_type && isConnectWise(userMspInfo.msp_type)
  );
  const canCreateTickets = Boolean(
    userMspInfo?.msp_type &&
      (
        isAutotask(userMspInfo.msp_type) ||
        isHalo(userMspInfo.msp_type) ||
        isConnectWise(userMspInfo.msp_type)
      )
  );
  const useAutotaskStatusOptions = Boolean(
    userMspInfo?.msp_type && isAutotask(userMspInfo.msp_type)
  );
  const useHaloStatusOptions = Boolean(
    userMspInfo?.msp_type && isHalo(userMspInfo.msp_type)
  );
  const useTicketDerivedStatusOptions = isConnectWisePsa;
  const ticketDerivedStatusOptions = useMemo<string[]>(
    () =>
      Array.from(
        new Set<string>(
          (tickets || [])
            .map((ticket: Ticket) => String(ticket?.status || "").trim())
            .filter((status: string) => Boolean(status))
        )
      ),
    [tickets]
  );
  const statusFilterOptions = useMemo<string[] | undefined>(() => {
    const uniqueNames = (names: string[]) => {
      const unique = Array.from(
        new Set(names.map((name) => String(name || "").trim()).filter(Boolean))
      );
      return unique.length > 0 ? unique : undefined;
    };

    if (useAutotaskStatusOptions) {
      return uniqueNames([...autotaskStatusOptions, ...ticketDerivedStatusOptions]);
    }
    if (useHaloStatusOptions) {
      return uniqueNames([...haloStatusOptions, ...ticketDerivedStatusOptions]);
    }
    if (!useTicketDerivedStatusOptions) {
      return uniqueNames(ticketDerivedStatusOptions);
    }
    const merged = isConnectWisePsa
      ? [...connectWiseStatusOptions, ...ticketDerivedStatusOptions]
      : ticketDerivedStatusOptions;
    return uniqueNames(merged);
  }, [
    useAutotaskStatusOptions,
    autotaskStatusOptions,
    useHaloStatusOptions,
    haloStatusOptions,
    useTicketDerivedStatusOptions,
    isConnectWisePsa,
    connectWiseStatusOptions,
    ticketDerivedStatusOptions,
    userMspInfo?.msp_type,
  ]);

  // Status filtering for Autotask queues is driven by fresh server fetches.
  // Keep customer filtering local for responsive narrowing within the fetched set.
  const ticketsForDisplay = useMemo(
    () => (isLoading && lastSettledTickets.length > 0 ? lastSettledTickets : tickets || []),
    [isLoading, lastSettledTickets, tickets]
  );

  const filteredTickets = useMemo(() => {
    if (!ticketsForDisplay || ticketsForDisplay.length === 0) {
      return ticketsForDisplay || [];
    }

    const hasCustomerFilter = displayFilters.customer !== undefined;
    const selectedCustomers = new Set(
      (displayFilters.customer || []).map((value) => normalizeFilterValue(value)).filter(Boolean)
    );
    const requestedStatuses = displayFilters.status;

    return ticketsForDisplay.filter((ticket: Ticket) => {
      if (requestedStatuses === undefined) {
        if (!isOpenTicketStatus(ticket.status, userMspInfo?.msp_type)) {
          return false;
        }
      } else if (
        !matchesSelectedStatus(ticket.status, requestedStatuses, {
          mspType: userMspInfo?.msp_type,
          strictMatch: false,
        })
      ) {
        return false;
      }

      const accountId = extractTicketAccountId(ticket);

      if (hasCustomerFilter) {
        if (selectedCustomers.size === 0) {
          return false;
        }
        if (!accountId || !selectedCustomers.has(accountId)) {
          return false;
        }
      }

      return true;
    });
  }, [
    ticketsForDisplay,
    displayFilters.customer,
    displayFilters.status,
    userMspInfo?.msp_type,
  ]);

  const totalCount = filteredTickets.length;
  const currentPageTickets = useMemo(
    () =>
      filteredTickets.slice((page - 1) * pageSize, page * pageSize),
    [filteredTickets, page, pageSize]
  );

  if (isLoading && lastSettledTickets.length === 0) {
    return <GlobalLoadingOverlay />;
  }

  // Don't return early - let TicketTable handle the empty state

  return (
    <Box
      sx={{
        p: 1,
        boxSizing: "border-box",
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        position: "relative",
      }}
    >
          <TicketTable
            title={groupName || categoryName || OPEN_TICKETS_LABEL}
            data={currentPageTickets}
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={handleListPageChange}
            onPageSizeChange={handleListPageSizeChange}
            onRowClick={serviceDeskInboxEnabled ? undefined : navigateToTicketDetail}
            openTicketDetailInPopup={serviceDeskInboxEnabled}
            buildTicketDetailPath={
              serviceDeskInboxEnabled ? buildTicketDetailPath : undefined
            }
            filters={displayFilters}
            onFilterChange={handleFilterChange}
            sort={sort}
            onSortChange={handleSortChange}
            showControls={true}
            mspType={userMspInfo?.msp_type}
            statusOptions={statusFilterOptions}
            onRefresh={handleRefreshTickets}
            showClosedSection={false}
            showNewTicketButton={canCreateTickets && Boolean(groupName || categoryName)}
            newTicketQueueName={groupName}
            newTicketCategoryName={categoryName}
          />
      {isLoading && lastSettledTickets.length > 0 && (
        <Box
          sx={{
            position: "absolute",
            inset: 8,
            borderRadius: 2,
            backgroundColor: "rgba(255, 255, 255, 0.45)",
            backdropFilter: "blur(2px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.25,
              borderRadius: 999,
              backgroundColor: "rgba(255, 255, 255, 0.92)",
              boxShadow: "0 12px 32px rgba(90, 94, 115, 0.12)",
              border: "1px solid rgba(155, 122, 239, 0.16)",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <ThreeDot color="#9B7AEF" size="small" />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default GroupQueue;
